/**
 * Stratigrafie preferite — per utente nell'org corrente.
 *
 * useFavoriteStratigraphies(): Set di stratigraphy id preferite + array
 * useToggleFavoriteStratigraphy(): mutation che aggiunge/rimuove dai preferiti
 *
 * Pattern speculare a useFavoriteMaterials. RLS: visibile/scrivibile solo
 * dal proprietario tramite policy `Users manage their own stratigraphy favorites`.
 */
import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';

export const useFavoriteStratigraphies = () => {
  const { user } = useAuth();
  const { currentOrganizationId } = useCurrentOrganization();

  const query = useQuery({
    queryKey: ['favorite-stratigraphies', user?.id, currentOrganizationId],
    enabled: !!user && !!currentOrganizationId,
    staleTime: 60_000,
    queryFn: async (): Promise<string[]> => {
      if (!user || !currentOrganizationId) return [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from('user_stratigraphy_favorites')
        .select('stratigraphy_id')
        .eq('user_id', user.id)
        .eq('organization_id', currentOrganizationId);
      if (error) {
        console.error('[useFavoriteStratigraphies] error:', error);
        return [];
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (data ?? []).map((r: any) => r.stratigraphy_id);
    },
  });

  const favoritesSet = useMemo(() => new Set(query.data ?? []), [query.data]);

  return {
    favoritesSet,
    favoritesIds: query.data ?? [],
    isLoading: query.isLoading,
  };
};

export const useToggleFavoriteStratigraphy = () => {
  const { user } = useAuth();
  const { currentOrganizationId } = useCurrentOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ stratigraphyId, isFavorite }: { stratigraphyId: string; isFavorite: boolean }) => {
      if (!user || !currentOrganizationId) throw new Error('Sessione non valida');
      if (isFavorite) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (supabase as any)
          .from('user_stratigraphy_favorites')
          .delete()
          .eq('user_id', user.id)
          .eq('organization_id', currentOrganizationId)
          .eq('stratigraphy_id', stratigraphyId);
        if (error) throw error;
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (supabase as any)
          .from('user_stratigraphy_favorites')
          .insert({
            user_id: user.id,
            organization_id: currentOrganizationId,
            stratigraphy_id: stratigraphyId,
          });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorite-stratigraphies', user?.id, currentOrganizationId] });
    },
  });
};
